import React, { useState, useEffect, useCallback } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Button, TextField, Box, CircularProgress, Typography,
    Autocomplete, Grid, Divider, FormControl, InputLabel,
    Select, MenuItem, Chip
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { orderListAPI, branchAPI } from '../../services/api';

const PAYMENT_METHODS = ['cash', 'bank_transfer', 'cheque', 'other'];

const PaymentDialog = ({ open, onClose, onSuccess, order }) => {
    const { t } = useTranslation();

    // ── Form state ────────────────────────────────────────────────────────
    const [amount, setAmount] = useState('');
    const [branch, setBranch] = useState(null);
    const [paymentDate, setPaymentDate] = useState('');
    const [paymentMethod, setPaymentMethod] = useState('cash');
    const [referenceNumber, setReferenceNumber] = useState('');
    const [notes, setNotes] = useState('');
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);

    // ── Reference data ────────────────────────────────────────────────────
    const [branches, setBranches] = useState([]);
    const [branchesLoading, setBranchesLoading] = useState(false);

    const remaining = order?.remainingBalance || 0;

    // ── Load branches ─────────────────────────────────────────────────────
    const loadBranches = useCallback(async () => {
        setBranchesLoading(true);
        try {
            const res = await branchAPI.getActiveBranches();
            const data = res.data?.data || res.data || [];
            setBranches(Array.isArray(data) ? data : []);
        } catch {
            setBranches([]);
        } finally {
            setBranchesLoading(false);
        }
    }, []);

    // ── Reset form on open ────────────────────────────────────────────────
    useEffect(() => {
        if (open) {
            loadBranches();
            setAmount('');
            setBranch(null);
            setPaymentDate(new Date().toISOString().split('T')[0]);
            setPaymentMethod('cash');
            setReferenceNumber('');
            setNotes('');
            setErrors({});
        }
    }, [open, loadBranches]);

    const handleClose = () => {
        setAmount('');
        setBranch(null);
        setErrors({});
        onClose();
    };

    // ── Validation ────────────────────────────────────────────────────────
    const validate = () => {
        const errs = {};
        const val = parseFloat(amount);

        if (!amount || isNaN(val) || val <= 0) {
            errs.amount = t('orderList.payment.amountInvalid', 'Payment amount must be greater than 0');
        } else if (val > remaining) {
            errs.amount = t('orderList.payment.amountExceedsBalance', 'Amount exceeds remaining balance');
        }

        if (!branch) {
            errs.branch = t('orderList.payment.branchRequired', 'Please select the branch the money comes from');
        }

        if (!paymentDate) {
            errs.paymentDate = t('orderList.payment.dateRequired', 'Payment date is required');
        }

        setErrors(errs);
        return Object.keys(errs).length === 0;
    };

    // ── Submit ────────────────────────────────────────────────────────────
    const handleSubmit = async () => {
        if (!validate()) return;
        setLoading(true);
        try {
            const payload = {
                amount: parseFloat(amount),
                branch: branch._id,
                paymentDate: paymentDate || undefined,
                paymentMethod,
                referenceNumber: referenceNumber.trim() || undefined,
                notes: notes.trim() || undefined,
            };

            await orderListAPI.addPayment(order._id, payload);
            toast.success(t('orderList.payment.success', 'Payment recorded successfully'));
            onSuccess();
        } catch (err) {
            const msg = err.response?.data?.message || t('orderList.payment.error', 'Failed to record payment');
            toast.error(msg);
        } finally {
            setLoading(false);
        }
    };

    if (!order) return null;

    return (
        <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
            <DialogTitle>{t('orderList.payment.title', 'Record Payment')}</DialogTitle>

            <DialogContent dividers>
                <Box display="flex" flexDirection="column" gap={2} pt={1}>

                    {/* ── Order Summary ──────────────────────────────── */}
                    <Box
                        sx={{
                            p: 2,
                            borderRadius: 1,
                            border: '1px solid',
                            borderColor: 'grey.200'
                        }}
                    >
                        <Grid container spacing={1}>
                            <Grid item xs={6}>
                                <Typography variant="caption" color="text.secondary">
                                    {t('orderList.orderNumber')}
                                </Typography>
                                <Typography variant="body2" fontWeight={600}>
                                    {order.orderNumber}
                                </Typography>
                            </Grid>
                            <Grid item xs={6}>
                                <Typography variant="caption" color="text.secondary">
                                    {t('orderList.supplier')}
                                </Typography>
                                <Typography variant="body2" fontWeight={600}>
                                    {order.supplier?.name || '—'}
                                </Typography>
                            </Grid>
                            <Grid item xs={4}>
                                <Typography variant="caption" color="text.secondary">
                                    {t('orderList.totalPrice')}
                                </Typography>
                                <Typography variant="body2" fontWeight={500}>
                                    {order.totalPrice?.toLocaleString()} {t('common.currency')}
                                </Typography>
                            </Grid>
                            <Grid item xs={4}>
                                <Typography variant="caption" color="text.secondary">
                                    {t('orderList.paidPrice')}
                                </Typography>
                                <Typography variant="body2" fontWeight={500} color="success.main">
                                    {order.paidPrice?.toLocaleString()} {t('common.currency')}
                                </Typography>
                            </Grid>
                            <Grid item xs={4}>
                                <Typography variant="caption" color="text.secondary">
                                    {t('orderList.remainingBalance')}
                                </Typography>
                                <Typography variant="body2" fontWeight={600} color="error.main">
                                    {remaining.toLocaleString()} {t('common.currency')}
                                </Typography>
                            </Grid>
                        </Grid>
                    </Box>

                    <Divider />

                    {/* ── Payment Amount ─────────────────────────────── */}
                    <TextField
                        label={t('orderList.payment.amount', 'Payment Amount')}
                        value={amount}
                        onChange={(e) => {
                            setAmount(e.target.value);
                            if (errors.amount) setErrors(p => ({ ...p, amount: '' }));
                        }}
                        type="number"
                        inputProps={{ min: 0.01, max: remaining, step: 0.01 }}
                        size="small"
                        fullWidth
                        required
                        error={Boolean(errors.amount)}
                        helperText={errors.amount}
                        autoFocus
                    />

                    {/* ── Branch (source of money) ──────────────────── */}
                    <Autocomplete
                        options={branches}
                        getOptionLabel={(o) => o.name || ''}
                        isOptionEqualToValue={(o, v) => o._id === v._id}
                        value={branch}
                        onChange={(_, v) => {
                            setBranch(v);
                            if (errors.branch) setErrors(p => ({ ...p, branch: '' }));
                        }}
                        loading={branchesLoading}
                        renderOption={(props, option) => (
                            <li {...props} key={option._id}>
                                <Box>
                                    <Typography variant="body2">{option.name}</Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {option.location}
                                    </Typography>
                                </Box>
                            </li>
                        )}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label={t('orderList.payment.branch', 'Source Branch (money collected from)')}
                                size="small"
                                required
                                error={Boolean(errors.branch)}
                                helperText={errors.branch}
                                InputProps={{
                                    ...params.InputProps,
                                    endAdornment: (
                                        <>
                                            {branchesLoading ? <CircularProgress size={16} /> : null}
                                            {params.InputProps.endAdornment}
                                        </>
                                    ),
                                }}
                            />
                        )}
                    />

                    {/* ── Payment Date ──────────────────────────────── */}
                    <TextField
                        label={t('orderList.payment.date', 'Payment Date')}
                        type="date"
                        value={paymentDate}
                        onChange={(e) => {
                            setPaymentDate(e.target.value);
                            if (errors.paymentDate) setErrors(p => ({ ...p, paymentDate: '' }));
                        }}
                        size="small"
                        fullWidth
                        required
                        error={Boolean(errors.paymentDate)}
                        helperText={errors.paymentDate}
                        InputLabelProps={{ shrink: true }}
                    />

                    {/* ── Payment Method ─────────────────────────────── */}
                    <FormControl size="small" fullWidth>
                        <InputLabel>{t('orderList.payment.method', 'Payment Method')}</InputLabel>
                        <Select
                            value={paymentMethod}
                            onChange={(e) => setPaymentMethod(e.target.value)}
                            label={t('orderList.payment.method', 'Payment Method')}
                        >
                            {PAYMENT_METHODS.map(method => (
                                <MenuItem key={method} value={method}>
                                    {t(`orderList.payment.methods.${method}`, method.replace('_', ' '))}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    {/* ── Reference Number (optional) ────────────────── */}
                    {(paymentMethod === 'cheque' || paymentMethod === 'bank_transfer') && (
                        <TextField
                            label={t('orderList.payment.referenceNumber', 'Reference / Cheque Number')}
                            value={referenceNumber}
                            onChange={(e) => setReferenceNumber(e.target.value)}
                            size="small"
                            fullWidth
                        />
                    )}

                    {/* ── Notes (optional) ───────────────────────────── */}
                    <TextField
                        label={t('orderList.payment.notes', 'Notes')}
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        size="small"
                        fullWidth
                        multiline
                        rows={2}
                    />

                    {/* ── Summary after payment ─────────────────────── */}
                    {amount && parseFloat(amount) > 0 && parseFloat(amount) <= remaining && (
                        <Box
                            sx={{
                                p: 1.5,
                                borderRadius: 1,
                                bgcolor: 'success.50',
                                border: '1px solid',
                                borderColor: 'success.200'
                            }}
                        >
                            <Typography variant="caption" color="text.secondary">
                                {t('orderList.payment.afterPayment', 'After this payment')}:
                            </Typography>
                            <Box display="flex" justifyContent="space-between" mt={0.5}>
                                <Typography variant="body2">
                                    {t('orderList.payment.newBalance', 'New Remaining Balance')}:
                                </Typography>
                                <Typography variant="body2" fontWeight={600}
                                    color={(remaining - parseFloat(amount)) > 0 ? 'error.main' : 'success.main'}
                                >
                                    {(remaining - parseFloat(amount)).toLocaleString()} {t('common.currency')}
                                </Typography>
                            </Box>
                            {(remaining - parseFloat(amount)) === 0 && (
                                <Chip
                                    label={t('orderList.payment.fullyPaid', 'Fully Paid!')}
                                    color="success"
                                    size="small"
                                    sx={{ mt: 1 }}
                                />
                            )}
                        </Box>
                    )}
                </Box>
            </DialogContent>

            <DialogActions>
                <Button onClick={handleClose} disabled={loading}>
                    {t('common.cancel')}
                </Button>
                <Button
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={loading || branchesLoading}
                    startIcon={loading ? <CircularProgress size={16} /> : null}
                >
                    {loading ? t('common.processing') : t('orderList.payment.submit', 'Record Payment')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default PaymentDialog;